import { RegularSlider } from '@warp-ds/components/react/slider';
import { Affix } from '@warp-ds/react';
import { TextField } from '@warp-ds/react/components/textfield';
import { memo, useEffect, useRef, useState } from 'react';
import styles from './SliderFilter.module.css';

type SliderFilterProps = {
    id: string;
    label: string;
    affix?: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange: (value: number) => void;
    renderValue?: (value: number) => string;
    className?: string;
    disabled?: boolean;
    showInput?: boolean;
    showMarks?: boolean;
    valueAsLabel?: boolean;
};

export const SliderFilter = memo(
    ({
        id,
        label,
        affix,
        value,
        min,
        max,
        step = 1,
        onChange,
        renderValue,
        className,
        disabled = false,
        showInput = true,
        showMarks = true,
        valueAsLabel = false,
    }: SliderFilterProps) => {
        const [displayValue, setDisplayValue] = useState<number>(value);
        const valueChangeTimeout = useRef<NodeJS.Timeout | null>(null);

        useEffect(() => {
            setDisplayValue(value);
        }, [value]);

        const handleChange = (newValue: number): void => {
            setDisplayValue(newValue);
            onChange(newValue);
        };

        const handleTextChange = (newValue: number): void => {
            setDisplayValue(Math.min(Math.max(newValue, min), max));

            if (valueChangeTimeout.current) {
                clearTimeout(valueChangeTimeout.current);
            }
            valueChangeTimeout.current = setTimeout((): void => {
                onChange(Math.min(Math.max(newValue, min), max));
            }, 800);
        };

        const valueText: string = renderValue?.(displayValue) ?? displayValue.toString();

        return (
            <div data-testid={id} className={className}>
                <RegularSlider
                    label={valueAsLabel ? valueText : label}
                    min={min}
                    max={max}
                    value={displayValue}
                    step={step}
                    onInput={setDisplayValue}
                    onChange={handleChange}
                    aria-label={label}
                    aria-valuetext={valueText}
                    disabled={disabled}
                    showMarks={showMarks}
                />
                {showInput && (
                    <div className="w-slider__input">
                        <TextField
                            type="number"
                            className={styles.textField}
                            value={displayValue.toString()}
                            onChange={(e) => handleTextChange(~~e.target.value)}
                            step={step}
                            min={min}
                            max={max}
                            aria-label={label}
                            aria-valuetext={valueText}>
                            {affix ? <Affix suffix label={affix} /> : null}
                        </TextField>
                    </div>
                )}
            </div>
        );
    },
);

SliderFilter.displayName = 'SliderFilter';
